import { Box, VStack } from '@chakra-ui/react';
import useOnScreen from '@common/hooks/useOnScreen';
import { usePropertiesModel } from '@common/models/PropertiesModel';
import React from 'react';
import { UI } from 'src/apps/invest/common/shells/UI';
import { usePropertiesDetailViewModel } from '../../models/PropertyDetailViewModel';
import { Loading } from '../common/Loading';
import { HACKED_TOGETHER_PROPERTY_IDS } from '../constants';
import { CapitalFull } from './CapitalFull';
import { CustomDetail } from './CustomDetail';
import { DealTerms } from './DealTerms';
import { Disclosures } from './Disclosures';
import { Documents } from './Documents';
import { Gallery } from './Gallery';
import { GalleryGrid } from './GalleryGrid';
import { GeneralInfo } from './Generallnfo';
import { HistoricalReturns } from './HistoricalReturns';
import { InvestCTA } from './InvestCTA';
import { InvestCtaMobileSticky } from './InvestCtaMobileSticky';
import { LeverageAndVolatility } from './LeverageAndVolatility';
import { Navigation } from './Navigation';
import { NearbyStores } from './NearbyStores';
import { NonAccreditedSlotsFull } from './NonAccreditedSlotsFull';
import { OfferingBreakdown } from './OfferingBreakdown';
import { RecentUpdates } from './RecentUpdates';
import { Roadmap } from './Roadmap';
import { Strategy } from './Strategy';
import { TenantLeasing } from './TenantLeasing';
import { TheMarket } from './TheMarket';
import { HouseIcon, NewConstructionIcon, StatAreaIcon, ThingsWeLike } from './ThingsWeLike';
import { TopEmployers } from './TopEmployers';
import { Helmet } from 'react-helmet';

export const PropertyDetailPage: React.FC = () => {
  const { property } = usePropertiesModel();
  const viewModel = usePropertiesDetailViewModel();
  const propertyImages = property?.sfr?.info.images ?? [];
  const [mobileCTAref, isVisible] = useOnScreen<HTMLDivElement>(50, 200);

  if (viewModel.pageState === 'LOADING')
    return (
      <UI>
        <Loading />
      </UI>
    );
  return (
    <UI>
      <Helmet>
        <title>
          {property?.sfr?.info.name
            ? `${property.sfr.info.name} - ${property.sfr.info.city}, ${property.sfr.info.state} ${property.sfr.info.zip}`
            : null}{' '}
          | Concreit
        </title>
      </Helmet>
      <Box
        mx="auto"
        paddingBottom={'50px'}
        display={'grid'}
        columnGap={['0px', '0px', '90px']}
        gridTemplateColumns={[`100%`, `100%`, `auto 350px`]}
        gridTemplateAreas={[`"header" "left-col"`, `"header" "left-col"`, `"header header" "left-col right-col"`]}>
        <Box gridArea={'header'} mb={['20px', propertyImages.length > 0 ? '70px' : 0]}>
          <Navigation />
          {propertyImages.length > 0 && <Gallery />}
        </Box>

        <Box gridArea={'left-col'} gap="20px">
          <Box mb={['40px', '100px']} paddingTop="30px">
            <GeneralInfo />
          </Box>
          <Box mb={['40px', '40px', '0px']} display={['flex', 'flex', 'none']} flexDirection="column" gap="80px">
            <Box ref={mobileCTAref}>
              {(viewModel.ctaState === 'INVEST' || viewModel.ctaState === 'UNAUTHENTICATED') && <InvestCTA />}
              {viewModel.ctaState === 'CAPITAL_FULL' && <CapitalFull />}
              {viewModel.ctaState === 'NON_ACCREDITED_SLOTS_FULL' && <NonAccreditedSlotsFull />}
            </Box>
            {(viewModel.ctaState === 'INVEST' || viewModel.ctaState === 'UNAUTHENTICATED') && (
              <Box
                position="fixed"
                bottom={!isVisible ? '0px' : '-100%'}
                left="0px"
                w="full"
                zIndex={2}
                backgroundColor={'white'}
                transition={'all 0.5s'}>
                <InvestCtaMobileSticky />
              </Box>
            )}
            <DealTerms />
          </Box>

          <CustomDetail
            propertyIds={[
              HACKED_TOGETHER_PROPERTY_IDS.dev.TheHartwell,
              HACKED_TOGETHER_PROPERTY_IDS.staging.TheDockery,
              HACKED_TOGETHER_PROPERTY_IDS.prod.TheDockery,
            ]}>
            <Box mb={['40px', '100px']}>
              <ThingsWeLike
                items={[
                  {
                    description: 'This property is a 2-year lease that ends on 12/31/2024',
                    highlight: '$2,490/mo rent',
                    image: <HouseIcon />,
                    title: 'Long-term lease signed',
                  },
                  {
                    description:
                      'This property is a new build by a top 10 builder, LGI Homes, and comes with a 10 year builder’s warranty and a 12 month workmanship warranty.',
                    highlight: "10 year builder's warranty",
                    image: <NewConstructionIcon />,
                    title: 'New construction with warranty',
                  },
                  {
                    description:
                      'Ranked #8 in Top 100 MSAs in the USA, and known as one of the fastest growing metros for the last decade.',
                    highlight: 'Atlanta Metro',
                    image: <StatAreaIcon />,
                    title: 'Top 100 Metropolitan Statistical Area',
                  },
                ]}
              />
            </Box>
          </CustomDetail>

          <Box mb={['40px', '100px']}>
            <RecentUpdates />
          </Box>

          <CustomDetail
            propertyIds={[
              HACKED_TOGETHER_PROPERTY_IDS.dev.TheHartwell,
              HACKED_TOGETHER_PROPERTY_IDS.staging.TheDockery,
              HACKED_TOGETHER_PROPERTY_IDS.prod.TheDockery,
            ]}>
            <Box mb={['40px', '100px']}>
              <Strategy />
            </Box>
          </CustomDetail>

          <Box mb={['40px', '100px']}>
            <HistoricalReturns />
          </Box>

          <Box mb={['40px', '100px']}>
            <LeverageAndVolatility />
          </Box>
          <Box mb={['40px', '100px']}>
            <TheMarket />
          </Box>
          <Box mb={['40px', '100px']}>
            <TopEmployers />
          </Box>
          <Box mb={['40px', '100px']}>
            <NearbyStores />
          </Box>
          {/* <Box mb={['40px', '100px']}>
            <NearbySchools />
          </Box> */}
          <Box mb={['40px', '100px']}>
            <Roadmap />
          </Box>
          <Box mb={['40px', '100px']}>
            <TenantLeasing />
          </Box>
          <Box mb={['40px', '100px']}>
            <OfferingBreakdown />
          </Box>
          <Box mb={['40px', '100px']}>
            <Documents />
          </Box>
          <Box mb={['40px', '100px']}>
            <Disclosures />
          </Box>
        </Box>
        <VStack
          display={['none', 'none', 'flex']}
          gridArea={'right-col'}
          gap={'50px'}
          alignItems={'flex-start'}
          position={'relative'}>
          <Box position="sticky" top="91px" backgroundColor="white" width="full" zIndex={2} paddingY="30px">
            {(viewModel.ctaState === 'INVEST' || viewModel.ctaState === 'UNAUTHENTICATED') && <InvestCTA />}
            {viewModel.ctaState === 'CAPITAL_FULL' && <CapitalFull />}
            {viewModel.ctaState === 'NON_ACCREDITED_SLOTS_FULL' && <NonAccreditedSlotsFull />}
          </Box>
          <DealTerms />
          <GalleryGrid />
        </VStack>
      </Box>
    </UI>
  );
};
